import data from "../../shared/sharedData";

export default {
  name: "Employer Information",
  sections: [
    {
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer profile to populate this section (optional)",
          prefill: {
            schema: "employerProfiles",
            type: "employer",
            formName: "9142A",
            formSection: "B",
          },
        },
        {
          code: "B.1",
          machine_name: "b1_emp_legal_name",
          widget: "text",
          label: "Legal Business Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Legal Business Name must contain at least 2 characters.",
            },
          ],
          maxLength: 120,
          tooltip: `Enter the full legal name of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application. The employer’s full legal name is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service (IRS).`,
        },
        {
          code: "B.2",
          machine_name: "b2_emp_dba",
          widget: "text",
          label: "Trade Name / Doing Business As (DBA), if applicable",
          validate: [
            {
              minLength: 5,
              message: "Trade Name/DBA must contain at least 5 characters.",
            },
          ],
          maxLength: 120,
          tooltip: `Enter the full trade name or “Doing Business As” (DBA) name, if applicable, of the business, person, association, firm, corporation, or organization (i.e., the employer filing this application). Do not include “DBA” in front of the full trade name entered or after the full legal name entered in Item B.1.`,
        },
        {
          code: "B.14",
          machine_name: "b12_emp_fein",
          widget: "text",
          label:
            "<span>Federal Employer Identification Number <i>(FEIN from IRS)</i></span>",
          validate: ["required", "fein", { minLength: 10 }],
          parse: "fein",
          maxLength: 10,
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) as assigned by the IRS. <u>Do not enter a social security number.</u>
          <br><b>Note:</b> All employers, <u>including private households</u>, MUST obtain an FEIN from the IRS before completing this application. Information on obtaining an FEIN can be found at <a target="_blank" href="https://www.IRS.gov"> www.IRS.gov</a>.`,
        },
        {
          code: "B.15",
          machine_name: "b13_emp_naics_description",
          widget: "text",
          label: "NAICS Description",
          hidden: true,
        },
        {
          code: "B.15",
          machine_name: "b13_emp_naics",
          widget: "naics",
          label: "NAICS Code",
          field_to_modify: "b13_emp_naics_description",
          validate: ["required"],
          tooltip: `Enter the four-digit North American Industry Classification System (NAICS) code that most closely corresponds to the employer’s primary economic or business activity, not the specific job opportunity being requested for temporary employment certification. For example, an employer primarily engaged in a combination of apple, citrus, and berry farming, would select NAICS Code “1113”. The first two digits identifies the major economic sector (e.g., 11 – Agriculture, Forestry, Fishing and Hunting); the third digit identifies the subsector (e.g., 1 – Crop Production); and the fourth digit identifies the industry group (e.g., 3 – Fruit and Tree Nut Farming). Additional information concerning the NAICS can be found at <a target="_blank" href="https://www.census.gov/naics/"> https://www.census.gov/naics/</a>.`,
        },
      ],
    },
    {
      fields: [
        {
          code: "B.3",
          machine_name: "empPrevTradeName",
          widget: "text",
          label: "Previous DBA, if applicable §",
          maxLength: 60,
          tooltip:
            "Enter the employer’s previous full trade name or DBA name, if applicable, of the business, person, association, firm, corporation, or organization used three years prior to the filing of this application.  Do not include “DBA” in front of the full trade name.  If the employer has additional DBAs used, prior to the filing of this application, insert additional names in Item B.4.",
        },
        {
          code: "B.4",
          machine_name: "empAddPrevTradeNames",
          widget: "text",
          label: "Previous DBA, if applicable §",
          maxLength: 60,
          tooltip:
            "Enter the employer’s previous full trade name(s) or DBA name(s), if applicable, of the business, person, association, firm, corporation, or organization used three years prior to the filing of this application.  Do not include “DBA” in front of the full trade name(s).",
        },
        {
          code: "B.5",
          machine_name: "b3_emp_address_1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 5,
              message: "Address 1 must contain at least 5 characters.",
            },
          ],
          info: {
            text: "Enter the business street address of the employer’s point of contact. A P.O. Box is a mailing address and not a valid physical address location and, when disclosed on this application, the CO will issue a NOD requesting removal of the P.O. Box mailing address and provide the employer with an opportunity to amend the application with a valid physical address location.",
            coloring: "grey",
            divider: false,
            css: {
              marginTop: 20,
              marginBottom: 10,
            },
          },
          maxLength: 60,
          tooltip:
            "Enter the street address of the employer’s principal place of business. The place of business must be a physical location and not a Post Office (P.O.) Box. A P.O. Box is a mailing address and not a valid physical address location and, when disclosed on this application, the CO will issue a NOD requesting removal of the P.O. Box mailing address and provide the employer with an opportunity to amend the application with a valid physical address location.",
        },
        {
          code: "B.6",
          machine_name: "b4_emp_address_2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this line to complete the employer’s street address. If no additional space if needed, enter “N/A” or leave blank.`,
        },
        {
          code: "B.10",
          machine_name: "b8_emp_country_id",
          widget: "select",
          label: "Country",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["b6_emp_state_id", "b9_emp_province"],
          tooltip: `Enter the country of the employer’s principal place of business.`,
        },
        {
          code: "B.7",
          machine_name: "b5_emp_city",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip: `Enter the city of the employer’s principal place of business.`,
          disabled: true,
          disableWhenField: "b8_emp_country_id",
        },
        {
          code: "B.8",
          machine_name: "b6_emp_state_id",
          widget: "select",
          label: "State",
          selection: data.state,
          validate: ["required"],
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          tooltip: `Enter the state of the employer’s principal place of business.`,
          isVisible: {
            when: "b8_emp_country_id",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
        },
        {
          code: "B.11",
          machine_name: "b9_emp_province",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              minLength: 5,
              message: "Province must contain at least 5 characters.",
            },
          ],
          tooltip: `Enter the province of the employer’s principal place of business, if applicable.`,
          isVisible: {
            when: "b8_emp_country_id",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "!==",
            hideStyles: true,
          },
        },
        {
          code: "B.9",
          machine_name: "b7_emp_zip",
          widget: "text",
          label: "Postal Code",
          validate: [
            "required",
            {
              postalCountry: "b8_emp_country_id",
              needsFieldValues: true,
              extraData: {
                valueOf: "b8_emp_country_id",
              },
            },
          ],
          disabled: true,
          disableWhenField: "b8_emp_country_id",
          maxLength: 10,
          tooltip: `Enter the postal (zip) code of the employer’s principal place of business.`,
        },
      ],
    },
    {
      fields: [
        {
          code: "B.12",
          machine_name: "b10_emp_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          maxLength: 15,
          tooltip: `Enter the area code and telephone number for the employer’s principal place of business. Include country code, if outside the United States.`,
        },
        {
          code: "B.13",
          machine_name: "b11_emp_phone_ext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip: `Enter the extension of the telephone number for the employer’s principal place of business, if applicable.`,
        },
      ],
    },
  ],
};
